// ============================================================
// MOONSHINE MASTERS — moonshine.js        THE RULES (pure)
//
// No React, no three, no Date.now() inside: every action that
// cares about time carries `now` (ms). That makes the whole
// game testable in Node and replayable, and it means a batch
// keeps working while the tab is closed (wall-clock stamps).
//
//   state  = moonshineReducer(state, action)
//
// THE RUN (2026-10-24, built for the tutorial - caP'n: "go here to mash in, send it here to start fermenting"):
//   pantry --MASH_IN--> the TUB (cooks MASH_S; MALT in the green = +1 jar) --POUR--> a BARREL (ferments; RUSH = Anima, rough)
//          --RUN--> THE STILL (still.js, 2026-10-23): it drips into THE JAR ROW - SWAP when the smell changes,
//          mind the FIRE (foam -> clog -> pop / BOOM), CLEAR the line -> it runs dry (or STOP) --BLEND--> jars
//          (+ the jars you left out = FEINTS) --DELIVER--> a Tome Contract
//   THE RIG: parts bought with Moon Coin (BUY / EQUIP); a BOOM busts the still -> REPAIR with scrap copper or coin.
//
// Two currencies only (ie-cross-game-economy, THE TWO-CURRENCY LAW):
//   mash  = this game's own coin (shown as "Moon Coin"; not registered at the Well yet - PRIME law 16).
//   Anima = NOT a wallet: it is the still's integrity (canon), a regenerating meter you spend to rush.
//   Feints and Scrap Copper are pantry ITEMS, not currencies (they're never earned as a number, only made/found).
// ============================================================
import { TUNING, RECIPES, INGREDIENTS, QUALITY, qRank, COIN, STILL, PARTS, START_RIG, THUMP_CHARGE, FLAVORS, CHARMS, SPECS, MASH, YEASTS, PROOF, SIGHTS, HEAT, CAMP, YARD_UP, DECOR, campLevel, START_RIG2, NEEDED_SLOTS, FREE, recipeOfMix, knownRecipe, AGE, TROPHY, RANK, MASTER, BLOOD, RATING } from "./moonshineData";
import { WISP_PAY } from "./missionDesign";
import { newRun, advance, swapJar, setFire, clearLine, stopRun, blend, emptyJar } from "./still";
import { contractOf, isGen, emptyLedger, cleanBoard, cleanLedger, offeredList, acceptedList, nextBoard, crewOf, cleanCrew, jobsDone } from "./missions";
import { say as words } from "./text";

const SAVE_VERSION = 3;
const noBarrels = () => Array.from({ length: TUNING.FERMENTERS }, () => null);

export function initialState(now = 0) {
  return {
    v: SAVE_VERSION,
    mash: TUNING.START_MASH,
    anima: TUNING.ANIMA_MAX, animaAt: now,
    khaoCap: TUNING.KHAO_START,
    bag: {},          // carried out in the wild - LOST if the beasts catch you
    pantry: {},       // banked in the yard - safe (feints + scrap copper live here too)
    jars: [],         // [{ r: recipeId, q: quality, rushed, cursed, fl: flavor|null }]
    tub: null,        // the mash tub: { recipe, startedAt, catalyst, size, malt } - cooking, then ready to pour
    barrels: noBarrels(),   // fermenting: [{ recipe, startedAt, rushed, catalyst, size, malt } | null]
    batch: null,      // THE RUN on the still (still.js newRun shape)
    rig: { ...START_RIG },  // the still's parts, one per slot
    owned: {},        // part id -> true (bought). Price-0 parts are always yours.
    broken: false,    // a BOOM busted the still: REPAIR before it runs again
    bang: null,       // { id, kind: "pop" | "boom" } - the world's FX watch this (not a rule)
    card: null,       // WILLIE'S CARD: the last blend's rating { r, score, parts, notes, jars, max, proof, id } - the yard's (everyone at the still sees it)
    best: {},         // recipe -> my best rating ever (personal)
    contracts: {},    // id -> "accepted" | "done" | "broken"  (an ENDLESS contract "g.<seat>.<n>" is only ever "accepted": it leaves the book when settled)
    board: {},        // THE ENDLESS BOARD (missions.js): seat -> how many times that seat has been filled (the next mission is born from it) - the yard's, like the book
    ledger: emptyLedger(),   // jobs finished per tier { fable, legendary, mythical }: opens the Legendary / Mythical seats - the yard's too
    specs: false,     // the Spectacular Spectacles are mine (earned from contract c5) - personal, saved
    eyes: false,      // wearing them: the Unstable Eyes are open (personal, NOT saved: every visit starts with them off)
    sights: {},       // THE UNSTABLE SIGHTS I've seen (sight id -> true; personal, saved, once per save - moonshineData SIGHTS)
    vowBack: {},      // a broken story vow that comes back: contract id -> the job count (ledger total) it waits for (the yard's, like the book)
    charm: null,      // a lit charm: { id, until (ms) } - slows the guards for the whole crew (the yard's, like the till)
    spookedUntil: 0,  // ms: after a catch you are too spooked to leave the yard until this dawn (only if TUNING.SPOOK_LOCK)
    // STARS + REVENUERS (2026-10-31, ROADMAP 33) - all the YARD's (a crew shares the heat, the lockbox and Willie's tab)
    stars: 0,         // 0..3: the heat on the camp (taking a hot contract puts it there)
    fuzzAt: 0,        // ms: when the fuzz arrives and raids (0 = nobody's coming)
    fuzzLeft: null,   // ms left on the fuzz clock while it's PAUSED (I'm working a friend's yard - my own camp waits, like my still)
    lockbox: { ing: {}, parts: [] },   // THE LOCKBOX: up to HEAT.LOCK_ING ingredients + HEAT.LOCK_PARTS spare rig parts - a raid can't touch it
    seized: false,    // a 3-star raid took the still: buy a new one (or take Willie's loan) before it runs again
    debt: 0,          // Willie's tab (the loan, or a death fine you couldn't pay): he takes HEAT.TAX from every contract until it's 0
    // THE REPLAY PACK (2026-10-31, ROADMAP 34-39, 41) - all personal except `master` (the yard's)
    book: {},         // recipe id -> true: the secret recipes I've found by FREE BLENDING at the tub
    shelf: [],        // THE AGING SHELF: [{ jar, at }] - better in real time (AGE); in camp, so a raid takes it
    trophies: [],     // THE TROPHY SHELF: legendary / 100-rated jars on show - a raid never takes one
    rank: 0,          // the highest DISTILLER RANK reached (index into RANK) - so a rank-up is said once
    master: false,    // MASTER MODE: the spooked-until-dawn lock is back, contracts pay MASTER.PAY x (the yard's - the keeper decides)
    brand: { name: "", art: "" },   // MY JAR LABEL: a brand name + a drawn label (art = LABEL.W x LABEL.H palette indexes)
    // THE YARD UPGRADES (2026-10-31, ROADMAP 40) - the yard's
    camp: 0,          // the camp's size (CAMP row): THE RATIO SYSTEM moves the woods out with it
    shack: 0,         // the shack's size (YARD_UP.shack row)
    decor: {},        // decoration id -> true
    stills: 1,        // how many stills the yard has (a second one is a yard upgrade)
    batch2: null, broken2: false, bang2: null,
    rig2: { ...START_RIG2 }, owned2: {},   // still 2's OWN parts (caP'n: bought piece by piece, nothing shared)   // THE SECOND STILL: the same shape as batch / broken / bang (actions carry still: 1)
    raid: null,       // { id, stars } - the last raid (a guest's screen watches the id: one busted = all busted)
    stats: { gathered: 0, banked: 0, jarred: 0, runs: 0, booms: 0, caught: 0, dumped: 0, malted: 0, wisps: 0, sights: 0, raids: 0, cleared: 0, jobs: 0 },   // lifetime counters (the tutorial reads the first four; booms..malted + sights wake the easter eggs - missionDesign EGGS; wisps = Stray Wisps brought home, it picks where the next one is)
    tut: { step: 0, done: false, gift: false, seen: {}, mark: { gathered: 0, banked: 0, jarred: 0, runs: 0 } },   // the tutorial's progress (tutorial.js owns its meaning)
    msg: null,        // { id, text } - the last thing the world said
    outbox: [],       // CO-OP: jars owed to the friends who brewed with you [{ to: uid, jar }] - sent, then cleared (OUTBOX_SENT)
    hands: [],        // CO-OP: what I handed a FRIEND's yard that she hasn't confirmed yet [{ id, sid, by, kind: STOCK|DELIVER_JARS, items|ct+jars, at }] - kept in the save until her receipt lands (HAND_DONE)
  };
}

// a contract in a book is only trusted if it is real: an endless one can only ever be "accepted" (it leaves the book when settled)
const okBook = (id, st) => { const c = contractOf(id); return !!c && (isGen(c) ? st === "accepted" : ["accepted", "done", "broken"].includes(st)); };
const cleanVowBack = (o) => Object.fromEntries(Object.entries(o && typeof o === "object" ? o : {}).filter(([id, n]) => { const c = contractOf(id); return c && c.reoffer && Number.isFinite(n) && n >= 0; }).map(([id, n]) => [id, Math.min(1e6, Math.floor(n))]));
const okRun = (b) => b && RECIPES[b.recipe] && Number.isFinite(b.startedAt);
// a saved/wired charm is only trusted if it is a real charm with a real end time
const okCharm = (c) => (c && typeof c === "object" && CHARMS[c.id] && Number.isFinite(c.until) ? { id: c.id, until: c.until } : null);
// THE HEAT, from a save or the wire: only real numbers / real ingredients / parts you own
const cleanStars = (n) => (Number.isFinite(n) ? Math.max(0, Math.min(HEAT.MAX, Math.floor(n))) : 0);
// what the lockbox holds: the shack's size decides (caP'n: a bigger shack = a bigger lockbox)
export const lockCap = (s) => ((YARD_UP.shack[(s && s.shack) || 0] || YARD_UP.shack[0]).box || { ing: HEAT.LOCK_ING, parts: HEAT.LOCK_PARTS });
const cleanLockbox = (b, owned, cap = { ing: HEAT.LOCK_ING, parts: HEAT.LOCK_PARTS }) => {
  const ing = {}; let room = cap.ing;
  for (const [k, v] of Object.entries((b && b.ing) || {})) if (INGREDIENTS[k] && !INGREDIENTS[k].errand && Number.isFinite(v) && v > 0 && room > 0) { const n = Math.min(room, Math.floor(v)); ing[k] = n; room -= n; }
  const parts = [...new Set(Array.isArray(b && b.parts) ? b.parts : [])].filter((id) => typeof id === "string" && owned[id] && Object.values(PARTS).some((sl) => sl[id] && sl[id].price > 0)).slice(0, cap.parts);
  return { ing, parts };
};
const cleanRaid = (r) => (r && Number.isFinite(r.id) && r.id > 0 ? { id: Math.floor(r.id), stars: cleanStars(r.stars) } : null);
const isRare = (k) => !!(k && INGREDIENTS[k] && INGREDIENTS[k].rare);
const okMalt = (m) => (m === "good" || m === "miss" ? m : null);
// EVERY STEP COUNTS (2026-10-25): the marks a tub / barrel carries - only real numbers survive (a missing mark = not measured = perfect)
const fin = (x, lo, hi) => (Number.isFinite(x) ? Math.max(lo, Math.min(hi, x)) : null);
const cleanMarks = (b) => {
  const m = {};
  const w = fin(b.water, 0, MASH.WATER_MAX); if (w != null) m.water = w;
  const bu = fin(b.burn, 0, 100); if (bu != null) m.burn = bu;
  const st = fin(b.stirAt, -1e15, 1e15); if (st != null) m.stirAt = st;
  const pi = fin(b.pitch, 0, 250); if (pi != null) m.pitch = pi;
  const ef = fin(b.eff, MASH.EFF_MIN, 1); if (ef != null) m.eff = ef;
  if (YEASTS[b.yeast]) m.yeast = b.yeast;
  return m;
};
const cleanRun = (b, cap) => ({ ...cleanMarks(b), recipe: b.recipe, startedAt: b.startedAt, rushed: !!b.rushed, malt: okMalt(b.malt),
  catalyst: isRare(b.catalyst) && !RECIPES[b.recipe].deviance ? b.catalyst : null, size: Number.isFinite(b.size) && b.size > 0 ? Math.floor(b.size) : cap });
// what a part costs HERE: still 2 (s.k2, while its fields are swapped in) pays price2 for the plain parts still 1 came with
export const partPrice = (s, slot, id) => { const p = PARTS[slot] && PARTS[slot][id]; return !p ? Infinity : s && s.k2 ? (p.price || p.price2 || 0) : p.price; };
export const partOwned = (s, slot, id) => !!PARTS[slot] && !!PARTS[slot][id] && (partPrice(s, slot, id) === 0 || !!(s.owned || {})[id]);
const cleanOwned2 = (o) => Object.fromEntries(Object.entries(o || {}).filter(([k, v]) => v === true && Object.values(PARTS).some((sl) => sl[k])).map(([k]) => [k, true]));
const cleanRig2 = (r, owned2) => Object.fromEntries(Object.keys(START_RIG2).map((slot) => { const id = r && r[slot]; return [slot, id && PARTS[slot][id] && (owned2[id] || (PARTS[slot][id].price === 0 && !PARTS[slot][id].price2)) ? id : START_RIG2[slot]]; }));
// a saved still run is only trusted if its shape is the 2026-10-23 one
const okStill = (b) => b && RECIPES[b.recipe] && Array.isArray(b.row) && b.row.length > 0 && Number.isFinite(b.prog) && Number.isFinite(b.at)
  && Number.isFinite(b.total) && b.total > 0 && b.rig && Number.isInteger(b.cur) && b.cur >= 0 && b.cur < b.row.length && STILL.FIRE[b.fire]
  && [b.foam, b.press, b.jars].every(Number.isFinite);

// a still run's extra marks (2026-10-25): trusted only as numbers / known ids
const cleanStillExtras = (b) => ({ eff: fin(b.eff, MASH.EFF_MIN, 1) == null ? 1 : fin(b.eff, MASH.EFF_MIN, 1),
  card: (({ water, burn, pitch, yeast }) => ({ water: fin(water, 0, MASH.WATER_MAX), burn: fin(burn, 0, 100), pitch: fin(pitch, 0, 250), yeast: YEASTS[yeast] ? yeast : null }))(b.card || {}),
  thump: b.thump == null ? null : !!b.thump, wept: Math.max(0, fin(b.wept, 0, 99) || 0), yeast: YEASTS[b.yeast] ? b.yeast : null, feints: Math.max(0, Math.min(STILL.FEINT_MAX, Math.floor(fin(b.feints, 0, 99) || 0))),
  clogs: Math.max(0, Math.floor(fin(b.clogs, 0, 999) || 0)), pops: Math.max(0, Math.floor(fin(b.pops, 0, 999) || 0)) });
// WILLIE'S CARD over the wire / from a save: only its shape
export const cleanCard = (c) => {
  if (!c || typeof c !== "object" || !RECIPES[c.r] || !Number.isFinite(c.score)) return null;
  const p = c.parts || {}, n = (x) => Math.max(0, Math.min(100, Math.round(Number.isFinite(x) ? x : 0)));
  return { id: Number.isFinite(c.id) ? c.id : 0, r: c.r, score: n(c.score), parts: { purity: n(p.purity), flavor: n(p.flavor), yield: n(p.yield), safety: n(p.safety) },
    notes: (Array.isArray(c.notes) ? c.notes : []).filter((t) => typeof t === "string").map((t) => t.slice(0, 80)).slice(0, 6),
    jars: Math.max(0, Math.floor(n(c.jars))), max: Math.max(0, Math.floor(n(c.max))), proof: Math.max(0, Math.floor(Number.isFinite(c.proof) ? Math.min(250, c.proof) : 0)), q: QUALITY.includes(c.q) ? c.q : "rough", cursed: !!c.cursed };
};

// merge a saved snapshot OVER the initial state so fields added later default cleanly
// lastSeen (ms) = when the game was last open: THE STILL WAITS FOR YOU (a run is paused while the game is closed - you
// have to be there to swap the jars); the tub and the barrels keep working on the wall clock.
export function hydrate(saved, now, lastSeen) {
  const base = initialState(now);
  if (!saved || typeof saved !== "object") return base;
  const s = { ...base, ...saved, v: SAVE_VERSION };
  delete s.k2;                                   // the still-2 swap marker never lives in a save
  // only ingredients that still exist; a count must be a positive whole number
  const clean = (o) => {
    const out = {};
    for (const [k, v] of Object.entries(o || {})) if (INGREDIENTS[k] && Number.isFinite(v) && v > 0) out[k] = Math.floor(v);
    return out;
  };
  s.pantry = clean(saved.pantry); delete s.pantry.wisp;
  s.sights = Object.fromEntries(SIGHTS.filter((g) => saved.sights && saved.sights[g.id] === true).map((g) => [g.id, true]));
  // THE BAG DOES NOT SURVIVE A RELOAD: position isn't saved, so a reload would teleport
  // you into the yard and bank everything you were carrying - a free escape from the beasts.
  s.bag = {};
  s.jars = Array.isArray(saved.jars) ? saved.jars.map(cleanJar).filter(Boolean) : [];
  s.card = cleanCard(saved.card);
  s.best = {}; for (const [k, v] of Object.entries(saved.best || {})) if (RECIPES[k] && Number.isFinite(v)) s.best[k] = Math.max(0, Math.min(100, Math.round(v)));
  s.contracts = {};
  for (const [id, st] of Object.entries(saved.contracts || {})) if (okBook(id, st)) s.contracts[id] = st;
  s.board = cleanBoard(saved.board);
  s.ledger = cleanLedger(saved.ledger);
  s.vowBack = cleanVowBack(saved.vowBack);
  s.khaoCap = Number.isFinite(s.khaoCap) ? Math.max(TUNING.KHAO_START, Math.min(TUNING.KHAO_MAX, Math.floor(s.khaoCap))) : TUNING.KHAO_START;
  s.tub = okRun(saved.tub) ? (({ rushed, ...t }) => t)(cleanRun(saved.tub, s.khaoCap)) : null;
  // the tub keeps cooking on the wall clock, but it can't catch while the game is closed
  if (s.tub && Number.isFinite(s.tub.stirAt)) { const seen = Number.isFinite(lastSeen) && lastSeen > s.tub.stirAt && lastSeen < now ? lastSeen : s.tub.stirAt; s.tub = { ...s.tub, stirAt: Math.min(now, s.tub.stirAt + (now - seen)) }; }   // the time it was closed slides out of the gap
  s.barrels = noBarrels().map((_, i) => (Array.isArray(saved.barrels) && okRun(saved.barrels[i]) ? cleanRun(saved.barrels[i], s.khaoCap) : null));
  // the rig: only real parts you own
  s.owned = {};
  for (const [k, v] of Object.entries(saved.owned || {})) if (v === true && Object.values(PARTS).some((sl) => sl[k])) s.owned[k] = true;
  s.rig = { ...START_RIG };
  for (const slot of Object.keys(START_RIG)) if (saved.rig && partOwned(s, slot, saved.rig[slot])) s.rig[slot] = saved.rig[slot];
  s.broken = !!saved.broken;
  s.charm = okCharm(saved.charm);
  // THE HEAT: like the still, the fuzz waits while the game is closed (the time it was closed slides out of the clock)
  s.stars = cleanStars(saved.stars);
  // a starred save comes back PAUSED (fuzzLeft = what was left when it was last seen); WAKE / the first TICK starts it again.
  // (review 2026-10-31: shifting fuzzAt here counted a title-screen reload twice, and ran the clock on the title screen)
  const full = s.stars ? HEAT.FUZZ_S[s.stars] * 1000 : 0;
  const left = !s.stars ? null : Number.isFinite(saved.fuzzLeft) && saved.fuzzLeft >= 0 ? saved.fuzzLeft
    : Number.isFinite(saved.fuzzAt) && saved.fuzzAt > 0 ? saved.fuzzAt - (Number.isFinite(lastSeen) && lastSeen > 0 && lastSeen < now ? lastSeen : now) : full;
  s.fuzzLeft = s.stars ? Math.max(0, Math.min(full, left)) : null;
  s.fuzzAt = 0;
  s.lockbox = cleanLockbox(saved.lockbox, s.owned);
  s.seized = saved.seized === true;
  s.debt = Number.isFinite(saved.debt) ? Math.max(0, Math.min(1e6, Math.floor(saved.debt))) : 0;
  s.raid = cleanRaid(saved.raid);
  s.book = Object.fromEntries(Object.entries(saved.book || {}).filter(([id, v]) => v === true && RECIPES[id] && RECIPES[id].hidden));
  s.shelf = (Array.isArray(saved.shelf) ? saved.shelf : []).map((x) => (x && cleanJar(x.jar) && !cleanJar(x.jar).cursed && Number.isFinite(x.at) && x.at > 0 ? { jar: cleanJar(x.jar), at: Math.max(now - AGE.MAX_STEPS * AGE.STEP_MIN * 60000, Math.min(x.at, now)) } : null)).filter(Boolean).slice(0, AGE.SLOTS);
  s.trophies = (Array.isArray(saved.trophies) ? saved.trophies : []).map(cleanJar).filter(trophyWorthy).slice(0, TROPHY.SLOTS);
  s.rank = Number.isFinite(saved.rank) ? Math.max(0, Math.min(RANK.length - 1, Math.floor(saved.rank))) : 0;
  s.master = saved.master === true;
  s.camp = campLevel(saved.camp);
  s.shack = saved.shack === 1 && YARD_UP.shack[1] ? 1 : 0;
  s.lockbox = cleanLockbox(saved.lockbox, s.owned, lockCap(s));       // again, now the shack's size is known
  s.owned2 = cleanOwned2(saved.owned2);
  s.rig2 = cleanRig2(saved.rig2, s.owned2);
  s.decor = Object.fromEntries(Object.keys(DECOR).filter((k) => saved.decor && saved.decor[k] === true).map((k) => [k, true]));
  s.stills = saved.stills === 2 ? 2 : 1;
  s.broken2 = s.stills === 2 && saved.broken2 === true;
  s.bang2 = null;
  s.batch2 = null;
  if (s.stills === 2 && okStill(saved.batch2)) {     // the second still waits while the game is closed too
    const num = (x) => (Number.isFinite(x) && x > 0 ? x : 0);
    const b = { ...saved.batch2, ...cleanStillExtras(saved.batch2), row: saved.batch2.row.map((j) => { const e = emptyJar(); return { f: num(j.f), h: num(j.h), H: num(j.H), t: num(j.t), c: num(j.c), cH: num(j.cH), popped: !!j.popped || e.popped }; }) };
    const upTo = Number.isFinite(lastSeen) && lastSeen > b.at && lastSeen < now ? lastSeen : b.at;
    const { run } = advance(b, upTo);
    s.batch2 = run.boom ? null : { ...run, at: now };
    if (run.boom) s.broken2 = true;
  }
  s.brand = { name: cleanName(saved.brand && saved.brand.name), art: cleanArt(saved.brand && saved.brand.art) };
  s.specs = saved.specs === true;
  s.eyes = false;
  s.bang = null;
  s.batch = null;
  if (okStill(saved.batch)) {
    // the still waits while the game is closed: play it forward to when the game was last seen, then pick up from NOW
    const num = (x) => (Number.isFinite(x) && x > 0 ? x : 0);
    const b = { ...saved.batch, ...cleanStillExtras(saved.batch), row: saved.batch.row.map((j) => { const e = emptyJar(); return { f: num(j.f), h: num(j.h), H: num(j.H), t: num(j.t), c: num(j.c), cH: num(j.cH), popped: !!j.popped || e.popped }; }) };
    const upTo = Number.isFinite(lastSeen) && lastSeen > b.at && lastSeen < now ? lastSeen : b.at;
    const { run } = advance(b, upTo);
    s.batch = run.boom ? null : { ...run, at: now };
    if (run.boom) s.broken = true;
  } else if (saved.batch && RECIPES[saved.batch.recipe]) {
    // an OLDER run (v1 brewing / v2 timer-cut): its clock meant something else - it comes back as a READY barrel
    const b = cleanRun({ ...saved.batch, startedAt: Number.isFinite(saved.batch.startedAt) ? saved.batch.startedAt : 0 }, s.khaoCap);
    const i = s.barrels.findIndex((x) => !x);
    if (i >= 0) s.barrels[i] = { ...b, startedAt: now - RECIPES[b.recipe].fermentS * 1000 };
  }
  s.anima = Number.isFinite(s.anima) ? Math.max(0, Math.min(TUNING.ANIMA_MAX, s.anima)) : TUNING.ANIMA_MAX;
  s.animaAt = Number.isFinite(s.animaAt) ? Math.min(s.animaAt, now) : now;      // a clock-skewed save must not stall regen
  s.mash = Number.isFinite(s.mash) ? Math.max(0, s.mash) : 0;
  s.spookedUntil = TUNING.SPOOK_LOCK && Number.isFinite(saved.spookedUntil) ? saved.spookedUntil : 0;
  const st = saved.stats || {};
  s.stats = Object.fromEntries(Object.keys(base.stats).map((k) => [k, Number.isFinite(st[k]) && st[k] > 0 ? Math.floor(st[k]) : 0]));
  const t = saved.tut || {};
  const mk = t.mark && typeof t.mark === "object" ? t.mark : null;
  s.tut = { step: Number.isFinite(t.step) && t.step >= 0 ? Math.floor(t.step) : 0, done: !!t.done, gift: !!t.gift,
    mark: mk ? Object.fromEntries(Object.keys(base.stats).map((k) => [k, Number.isFinite(mk[k]) ? mk[k] : 0])) : { ...s.stats },
    seen: t.seen && typeof t.seen === "object" ? Object.fromEntries(Object.entries(t.seen).filter(([, v]) => v === true)) : {} };
  s.msg = null;
  s.outbox = Array.isArray(saved.outbox) ? saved.outbox.map((o, i) => o && typeof o.to === "string" && o.to && cleanJar(o.jar) ? { id: typeof o.id === "string" && /^[a-z0-9]{1,40}$/.test(o.id) ? o.id : `gold${i}x${Math.floor(now).toString(36)}`, to: o.to.slice(0, 128), jar: cleanJar(o.jar) } : null).filter(Boolean).slice(0, 64) : [];
  s.hands = Array.isArray(saved.hands) ? saved.hands.map(cleanHand).filter(Boolean).slice(0, 16) : [];
  return s;
}

// a hand-over still in the post (see `hands`): anything malformed is dropped, never trusted
const cleanHand = (h) => {
  if (!h || typeof h.id !== "string" || !/^[a-z0-9]{1,40}$/.test(h.id) || typeof h.sid !== "string" || !/^[A-Za-z0-9_-]{1,40}$/.test(h.sid) || typeof h.by !== "string" || !h.by) return null;
  const at = Number.isFinite(h.at) ? h.at : 0;
  if (h.kind === "STOCK") {
    const items = {};
    for (const [k, v] of Object.entries(h.items || {})) if (INGREDIENTS[k] && Number.isFinite(v) && v > 0) items[k] = Math.min(999, Math.floor(v));
    return Object.keys(items).length ? { id: h.id, sid: h.sid, by: h.by.slice(0, 128), kind: "STOCK", items, at } : null;
  }
  if (h.kind === "DELIVER_JARS") {
    const jars = (Array.isArray(h.jars) ? h.jars : []).map(cleanJar).filter(Boolean).slice(0, 12);
    return jars.length && typeof h.ct === "string" && contractOf(h.ct) ? { id: h.id, sid: h.sid, by: h.by.slice(0, 128), kind: "DELIVER_JARS", ct: h.ct, jars, at } : null;
  }
  return null;
};

// a brand name: printable, short
const cleanName = (t) => String(t || "").replace(/[^\x20-\x7E]/g, "").trim().slice(0, LABEL_NAME);
const LABEL_NAME = 24;
// a drawn label: exactly LABEL_W x LABEL_H palette digits (anything else = blank)
export const LABEL_W = 16, LABEL_H = 12;
const cleanArt = (a) => (typeof a === "string" && a.length === LABEL_W * LABEL_H && /^[0-7]+$/.test(a) ? a : "");
// a jar: sc = Willie's rating (0-100, null on old jars), pf = its proof (null on old jars)
function cleanJar(j) {
  if (!(j && RECIPES[j.r] && QUALITY.includes(j.q))) return null;
  const sc = Number.isFinite(j.sc) ? Math.max(0, Math.min(100, Math.round(j.sc))) : null, pf = Number.isFinite(j.pf) ? Math.max(0, Math.min(250, Math.round(j.pf))) : null;
  const ag = Number.isFinite(j.ag) ? Math.max(0, Math.min(AGE.MAX_STEPS, Math.floor(j.ag))) : 0, lb = typeof j.lb === "string" ? cleanName(j.lb) : "";
  const out = { r: j.r, q: j.q, rushed: !!j.rushed, cursed: !!j.cursed, fl: FLAVORS[j.fl] ? j.fl : null, sc, pf };
  if (ag) out.ag = ag;          // aging steps it has had on the shelf
  if (lb) out.lb = lb;          // the brand on its label
  if (QUALITY.includes(j.cap)) out.cap = j.cap;   // the best it may ever be (rushed / the shortcut / turbo yeast / the House Blend) - aging respects it
  if (j.tr === true) out.tr = true;               // it came down off the trophy shelf: a show piece, never sold
  return out;
}

// ── THE SHARED YARD (co-op step 2, 2026-10-24) ──────────────────
// On a server with friends, ONE brewer keeps the yard (the first one in: moonshineYard.js picks) and everyone
// works HER tub, barrels, still and shelf. These are the yard's fields; everything else (your satchel, your jars,
// your Moon Coin, your contracts, your tutorial) stays yours. Pure: moonshineYard.js does the sending.
// THE TILL (caP'n 2026-10-24): "it should have shared currency if everyone's playing together ... a dedicated business
// expense everyone shares" - so the Moon Coin and the contract book are the YARD's too: one till, one book, while you're
// together. Your own coin and book at home are untouched (achievements are a different thing - not here at all).
export const YARD_KEYS = ["tub", "barrels", "batch", "broken", "bang", "rig", "owned", "pantry", "anima", "animaAt", "khaoCap", "mash", "contracts", "board", "ledger", "crew", "charm", "card", "vowBack", "stars", "fuzzAt", "fuzzLeft", "lockbox", "seized", "debt", "raid", "master", "camp", "shack", "decor", "stills", "batch2", "broken2", "bang2", "rig2", "owned2"];
export const yardOf = (s) => Object.fromEntries(YARD_KEYS.map((k) => [k, k === "crew" ? crewOf(s) : s[k] === undefined ? null : s[k]]));
export const withYard = (s, y) => (y ? { ...s, ...y } : s);
// a yard that came over the wire: trust nothing, keep the shape (NO playing forward - it's live, not a save)
export function cleanYard(raw) {
  if (!raw || typeof raw !== "object") return null;
  const base = initialState(0), y = {};
  const num = (x, d) => (Number.isFinite(x) ? x : d);
  const cleanItems = (o) => { const out = {}; for (const [k, v] of Object.entries(o || {})) if (INGREDIENTS[k] && Number.isFinite(v) && v > 0) out[k] = Math.floor(v); return out; };
  y.khaoCap = Math.max(TUNING.KHAO_START, Math.min(TUNING.KHAO_MAX, Math.floor(num(raw.khaoCap, TUNING.KHAO_START))));
  y.tub = okRun(raw.tub) ? (({ rushed, ...t }) => t)(cleanRun(raw.tub, y.khaoCap)) : null;
  y.barrels = base.barrels.map((_, i) => (Array.isArray(raw.barrels) && okRun(raw.barrels[i]) ? cleanRun(raw.barrels[i], y.khaoCap) : null));
  y.batch = okStill(raw.batch) ? { ...raw.batch, ...cleanStillExtras(raw.batch), row: raw.batch.row.map((j) => { const e = emptyJar(); const n = (x) => (Number.isFinite(x) && x > 0 ? x : 0); return { f: n(j.f), h: n(j.h), H: n(j.H), t: n(j.t), c: n(j.c), cH: n(j.cH), popped: !!j.popped || e.popped }; }) } : null;
  y.broken = !!raw.broken;
  y.bang = raw.bang && Number.isFinite(raw.bang.id) && ["pop", "boom"].includes(raw.bang.kind) ? { id: raw.bang.id, kind: raw.bang.kind } : null;
  y.rig = { ...START_RIG };
  for (const slot of Object.keys(START_RIG)) if (raw.rig && PARTS[slot] && PARTS[slot][raw.rig[slot]]) y.rig[slot] = raw.rig[slot];
  y.pantry = cleanItems(raw.pantry);
  y.anima = Math.max(0, Math.min(TUNING.ANIMA_MAX, num(raw.anima, TUNING.ANIMA_MAX)));
  y.mash = Math.max(0, num(raw.mash, 0));
  y.owned = {};
  for (const [k, v] of Object.entries(raw.owned || {})) if (v === true && Object.values(PARTS).some((sl) => sl[k])) y.owned[k] = true;
  y.contracts = {};
  for (const [id, st] of Object.entries(raw.contracts || {})) if (okBook(id, st)) y.contracts[id] = st;
  y.board = cleanBoard(raw.board);
  y.ledger = cleanLedger(raw.ledger);
  y.vowBack = cleanVowBack(raw.vowBack);
  y.crew = cleanCrew(raw.crew);
  y.animaAt = num(raw.animaAt, 0);
  y.card = cleanCard(raw.card);
  y.charm = okCharm(raw.charm);
  y.stars = cleanStars(raw.stars);
  y.fuzzAt = y.stars && Number.isFinite(raw.fuzzAt) ? raw.fuzzAt : 0;
  y.fuzzLeft = y.stars && Number.isFinite(raw.fuzzLeft) && raw.fuzzLeft >= 0 ? Math.min(raw.fuzzLeft, HEAT.FUZZ_S[y.stars] * 1000) : null;
  y.lockbox = cleanLockbox(raw.lockbox, y.owned);
  y.seized = raw.seized === true;
  y.debt = Number.isFinite(raw.debt) ? Math.max(0, Math.min(1e6, Math.floor(raw.debt))) : 0;
  y.raid = cleanRaid(raw.raid);
  y.master = raw.master === true;
  y.camp = campLevel(raw.camp);
  y.shack = raw.shack === 1 ? 1 : 0;
  y.lockbox = cleanLockbox(raw.lockbox, y.owned, lockCap(y));
  y.owned2 = cleanOwned2(raw.owned2);
  y.rig2 = cleanRig2(raw.rig2, y.owned2);
  y.decor = Object.fromEntries(Object.keys(DECOR).filter((k) => raw.decor && raw.decor[k] === true).map((k) => [k, true]));
  y.stills = raw.stills === 2 ? 2 : 1;
  y.batch2 = y.stills === 2 && okStill(raw.batch2) ? { ...raw.batch2, ...cleanStillExtras(raw.batch2), row: raw.batch2.row.map((j) => { const n = (x) => (Number.isFinite(x) && x > 0 ? x : 0); return { f: n(j.f), h: n(j.h), H: n(j.H), t: n(j.t), c: n(j.c), cH: n(j.cH), popped: !!j.popped }; }) } : null;
  y.broken2 = y.stills === 2 && raw.broken2 === true;
  y.bang2 = raw.bang2 && Number.isFinite(raw.bang2.id) && ["pop", "boom"].includes(raw.bang2.kind) ? { id: raw.bang2.id, kind: raw.bang2.kind } : null;
  return y;
}
// the keeper's clock and mine differ: slide every wall-clock field of a yard by `ms` (theirs -> mine)
export function shiftYard(y, ms) {
  if (!y || !ms) return y;
  const sh = (b) => (b ? { ...b, startedAt: b.startedAt - ms, ...(Number.isFinite(b.stirAt) ? { stirAt: b.stirAt - ms } : {}) } : b);
  return { ...y, tub: sh(y.tub), barrels: y.barrels.map(sh), batch: y.batch ? { ...y.batch, at: y.batch.at - ms } : null, batch2: y.batch2 ? { ...y.batch2, at: y.batch2.at - ms } : null, animaAt: y.animaAt - ms, charm: y.charm ? { ...y.charm, until: y.charm.until - ms } : null, fuzzAt: y.fuzzAt ? y.fuzzAt - ms : 0 };
}
// which actions are the YARD's (a guest sends these to the keeper instead of doing them at home)
export const YARD_ACTIONS = ["MASH_IN", "STIR", "MALT", "POUR", "RUSH", "RUN", "SWAP", "FIRE", "CLEAR", "STOP", "BLEND", "REPAIR", "STOCK", "BUY", "EQUIP", "ACCEPT", "ABANDON", "DELIVER_JARS", "CHARM", "LOCK", "UNLOCK", "NEW_STILL", "LOAN", "FINE", "UPGRADE"];
// whose words a guest should hear too (the yard's news, not the keeper's own satchel)
export const YARD_NEWS = ["TICK", "MASH_IN", "STIR", "MALT", "POUR", "RUSH", "RUN", "SWAP", "FIRE", "CLEAR", "STOP", "BLEND", "REPAIR", "STOCK", "BUY", "EQUIP", "ACCEPT", "ABANDON", "DELIVER", "DELIVER_JARS", "CHARM", "LOCK", "UNLOCK", "NEW_STILL", "LOAN", "FINE", "STARS_CLEAR", "UPGRADE"];

const say = (s, text, icon) => ({ ...s, msg: { id: (s.msg ? s.msg.id : 0) + 1, text, icon: icon || null } });   // icon = a picture name (public/icons)
// a contract that gives the Spectacles (reward.specs): they are mine for good. Returns the new state + a line to add to the message.
const withSpecs = (s, c) => (c && c.reward && c.reward.specs && !s.specs ? { s: { ...s, specs: true }, note: ` You earned the ${SPECS.name}! Press ${SPECS.key} to wear them.` } : { s, note: "" });
const bump = (o, k, n = 1) => ({ ...o, [k]: (o[k] || 0) + n });
const stat = (s, k, n = 1) => ({ ...s, stats: { ...s.stats, [k]: (s.stats[k] || 0) + n } });
const tidy = (o) => { const out = { ...o }; for (const k of Object.keys(out)) if (out[k] <= 0) delete out[k]; return out; };

// ── time of day (wall-clock, so every client agrees) ─────────
export const dayPhase = (nowMs) => ((nowMs / 1000) % TUNING.DAY_S) / TUNING.DAY_S;
// matches the sky in World3D (dark from 0.60 to the dawn ramp at ~0.05)
// the next dawn (the moment isNight turns false), strictly after `nowMs`
export function nextDawn(nowMs) {
  const cycle = TUNING.DAY_S * 1000, k = Math.floor(nowMs / cycle);
  let d = k * cycle + 0.05 * cycle;
  if (d <= nowMs) d += cycle;
  return d;
}
export const isSpooked = (s, nowMs) => nowMs < (s.spookedUntil || 0);
export const isNight = (nowMs) => { const p = dayPhase(nowMs); return p >= 0.62 || p < 0.05; };

// ── the stations ─────────────────────────────────────────────
export const elapsedMs = (b, now) => Math.max(0, now - b.startedAt);
// THE TUB
export const tubPhase = (t, now) => (!t ? "empty" : elapsedMs(t, now) < TUNING.MASH_S * 1000 ? "cooking" : "cooked");
// the malt window, as a share of the cook: can you add it right now, and is it in the green?
export const maltShare = (t, now) => (t ? elapsedMs(t, now) / (TUNING.MASH_S * 1000) : 0);
export const canMalt = (t, now) => !!t && !t.malt && !RECIPES[t.recipe].deviance && tubPhase(t, now) === "cooking";
// A BARREL
// the yeast sets the pace; a cold pitch is just slower (a missing mark = bread, pitched right)
export const fermentMs = (b) => RECIPES[b.recipe].fermentS * 1000 * (YEASTS[b.yeast] ? YEASTS[b.yeast].time : 1)
  * (Number.isFinite(b.pitch) && b.pitch < MASH.PITCH[0] ? MASH.PITCH_COLD_TIME : 1);

// ── THE MASH, step by step (2026-10-25) ──
// the stir: the bottom heats at `rate` a second since the last stir; over CATCH it burns. Burn over a gap of L seconds (closed form).
export function burnOver(L, rate) {
  if (!(L > 0) || !(rate > 0)) return 0;
  const t1 = MASH.CATCH / rate, t2 = 1 / rate;
  if (L <= t1) return 0;
  const a = Math.min(L, t2);
  let b = (rate / 2) * (a * a - t1 * t1) - MASH.CATCH * (a - t1);
  if (L > t2) b += (1 - MASH.CATCH) * (L - t2);
  return b;
}
// how fast THIS mash catches: a thick one (under the line) catches faster; the sugar shortcut has no grain to burn
export const stirRate = (t) => (RECIPES[t.recipe].deviance ? 0 : MASH.STIR_RATE * (1 + MASH.THICK_STIR * Math.max(0, 1 - (Number.isFinite(t.water) ? t.water : 1))));
export const cookEnd = (t) => t.startedAt + TUNING.MASH_S * 1000;
// the burn so far (up to `now`, never past the end of the cook) - null if this tub isn't measured (an old save)
export function tubBurn(t, now) {
  if (!t || !Number.isFinite(t.stirAt)) return null;
  const end = Math.min(now, cookEnd(t));
  return (t.burn || 0) + (end > t.stirAt ? burnOver((end - t.stirAt) / 1000, stirRate(t)) : 0);
}
// how hot the bottom is right now (0..1; over CATCH = burning) - the HUD's needle
export const bottomHeat = (t, now) => (t && Number.isFinite(t.stirAt) && now < cookEnd(t) ? Math.min(1, stirRate(t) * Math.max(0, now - t.stirAt) / 1000) : 0);
// the mash's temperature (degrees F) after the cook: it cools toward the air. During the cook it's on the boil.
export const mashTemp = (t, now) => (!t ? MASH.COOL_TO : now < cookEnd(t) ? MASH.COOL_FROM + 20 : MASH.COOL_TO + (MASH.COOL_FROM - MASH.COOL_TO) * Math.exp(-(now - cookEnd(t)) / 1000 / MASH.COOL_S));
export const pitchZone = (T) => (T > MASH.PITCH_HOT ? "hot" : T > MASH.PITCH[1] ? "warm" : T < MASH.PITCH[0] ? "cold" : "good");
// how much of the wash converts: the water line and the pitch (1 = all of it)
export function mashEff(water, pitch) {
  let e = 1;
  if (Number.isFinite(water)) { const d = water - 1, off = Math.max(0, Math.abs(d) - MASH.WATER_OK); e *= 1 - (d > 0 ? MASH.THIN_EFF : MASH.THICK_EFF) * off; }
  if (Number.isFinite(pitch) && pitch > MASH.PITCH_HOT) e *= MASH.PITCH_COOKED_EFF;
  return Math.max(MASH.EFF_MIN, Math.min(1, e));
}
export const barrelPhase = (b, now) => (!b ? "empty" : elapsedMs(b, now) < fermentMs(b) ? "working" : "ready");
export const freeBarrel = (s) => s.barrels.findIndex((b) => !b);
export const readyAt = (b) => b.startedAt + fermentMs(b);
export function readyBarrel(s, now) {        // the barrel that has been READY longest (a rushed one counts from its rush)
  let best = -1;
  s.barrels.forEach((b, i) => { if (b && barrelPhase(b, now) === "ready" && (best < 0 || readyAt(b) < readyAt(s.barrels[best]))) best = i; });
  return best;
}

export function canBrew(s, recipeId) {
  const r = RECIPES[recipeId];
  return !!r && Object.entries(r.needs).every(([k, n]) => (s.pantry[k] || 0) >= n);
}

// ── contracts ────────────────────────────────────────────────
// the story contracts in order, the easter eggs that are out, then the ENDLESS ones by tier (missions.js owns the how)
export function offered(s) { return offeredList(s); }
export const accepted = (s) => acceptedList(s);
// a contract is DONE: a story one or an egg stays "done" in the book; an endless one leaves the book, its seat moves on to the next number, the ledger counts it
const finishContract = (s, c) => {
  if (!isGen(c)) return { ...s, contracts: { ...s.contracts, [c.id]: "done" } };
  const { [c.id]: _gone, ...rest } = s.contracts;
  return { ...s, contracts: rest, board: nextBoard(s, c), ledger: { ...s.ledger, [c.tier]: ((s.ledger && s.ledger[c.tier]) || 0) + 1 } };
};

// Which jars would pay this contract? Returns indices or null. The jars you
// keep are the best ones: lowest-quality qualifying jars go first. Cursed jars never count.
// `hold` (2026-10-30) = the proofs OTHER accepted jobs ask for: a jar already at one of those goes last, so a job that
// doesn't care about proof never spends the jar a proof job needs. (heldProofs builds it from the book.)
export const heldProofs = (s, exceptId) => acceptedList(s).filter((c) => c.id !== exceptId && c.req && Number.isFinite(c.req.proof)).map((c) => c.req.proof);
const atHeld = (j, hold) => Number.isFinite(j.pf) && hold.some((p) => Math.abs(j.pf - p) <= PROOF.TOL);
export function pickJars(jars, req, hold = []) {
  const ok = jars
    .map((j, i) => ({ j, i }))
    .filter(({ j }) => {
      const rec = RECIPES[j.r];
      if (j.cursed || j.tr) return false;      // a show piece off the trophy shelf is never sold (review: the shelf was a raid-proof vault)
      if (req.recipe && j.r !== req.recipe) return false;
      if (req.tag && rec.tag !== req.tag) return false;
      if (req.flavor && j.fl !== req.flavor) return false;
      if (req.minQ && qRank(j.q) < qRank(req.minQ)) return false;
      if (req.notRushed && j.rushed) return false;
      if (Number.isFinite(req.proof) && !(Number.isFinite(j.pf) && Math.abs(j.pf - req.proof) <= PROOF.TOL)) return false;   // "at 100 proof" = within TOL
      return true;
    })
    // forage-only and flavored jars go LAST (they may be saved for the contract that needs one), then lowest quality first
    .sort((a, b) => (!Number.isFinite(req.proof) ? (atHeld(a.j, hold) ? 1 : 0) - (atHeld(b.j, hold) ? 1 : 0) : 0)
      || (RECIPES[a.j.r].forageOnly ? 1 : 0) - (RECIPES[b.j.r].forageOnly ? 1 : 0) || (a.j.fl ? 1 : 0) - (b.j.fl ? 1 : 0) || qRank(a.j.q) - qRank(b.j.q));
  const need = req.jars || 1, forage = req.forageOnlyJars || 0;
  const f = ok.filter(({ j }) => RECIPES[j.r].forageOnly);
  if (f.length < forage) return null;
  const chosen = f.slice(0, forage);
  const rest = ok.filter((o) => !chosen.includes(o));
  while (chosen.length < need && rest.length) chosen.push(rest.shift());
  return chosen.length >= need ? chosen.map((o) => o.i) : null;
}

function breakVow(s, kind) {
  let out = s, hit = null;
  for (const c of acceptedList(s)) {
    if (c.vow !== kind) continue;
    if (isGen(c)) { const { [c.id]: _gone, ...rest } = out.contracts; out = { ...out, contracts: rest, board: nextBoard(out, c) }; }   // an endless one: the visitor leaves, the seat moves on
    else out = { ...out, contracts: { ...out.contracts, [c.id]: "broken" }, vowBack: c.reoffer ? { ...(out.vowBack || {}), [c.id]: jobsDone(out) + c.reoffer } : out.vowBack };
    hit = c;
  }
  return hit ? say(out, `The vow is broken. ${hit.client} walks away - no payout for "${hit.ask}"` + (hit.reoffer ? `. Finish ${hit.reoffer} more jobs and he'll ask again.` : "")) : out;
}
const spend = (s, n, now) => ({ ...s, anima: s.anima - n, animaAt: s.anima >= TUNING.ANIMA_MAX ? now : s.animaAt });
const banged = (s, kind) => ({ ...s, bang: { id: (s.bang ? s.bang.id : 0) + 1, kind } });

// THE STILL, settled to `now`: plays the run forward and lands whatever happened on the way
// (a clog, a pop, a BOOM, running dry). TICK keeps the result only when something HAPPENED -
// otherwise the run's clock just keeps going and the HUD peeks at it (no React churn every second).
function settle(s, now) {
  if (!s.batch) return { s, events: [] };
  const { run, events } = advance(s.batch, now);
  let out = { ...s, batch: run };
  for (const e of events) {
    if (e === "clog") out = say(out, "The line's CLOGGED! Clear it (C) before the pressure blows.", "still");
    if (e === "pop") out = banged(say(spend(out, Math.min(out.anima, STILL.POP_ANIMA), now), "POP! The flour paste blew - steam everywhere. The jar under the spout went with it.", "still"), "pop");
    if (e === "dry") out = say(out, "The still ran dry - time to blend your jars.", "still");
    if (e === "boom") {
      out = stat(banged(say(spend({ ...out, batch: null, broken: true }, Math.min(out.anima, STILL.BOOM_ANIMA), now),
        "KA-BOOM! The clamps held. The still didn't. Patch it with scrap copper, or buy new parts.", "still"), "boom"), "booms");
      out = breakVow(out, "flawless");
    }
  }
  return { s: out, events };
}

// ── STARS + REVENUERS (2026-10-31, ROADMAP 33 - caP'n's rules) ───────────
// how long until the fuzz gets here (ms; null = nobody's coming) - the HUD's countdown
export const fuzzIn = (s, now) => (s.stars > 0 ? (s.fuzzLeft != null ? s.fuzzLeft : Math.max(0, (s.fuzzAt || 0) - now)) : null);
// how long the whole crew has to stay out past the lanterns, in one go, to lose them (ms)
export const clearMs = (s) => (s.stars > 0 ? s.stars * HEAT.CLEAR_S * 1000 : 0);
// stars land the moment you TAKE a hot contract. Hotter wins; a clock that's already closer stays (the fuzz never slows down).
function heatUp(s, stars, now) {
  const n = Math.max(s.stars || 0, cleanStars(stars));
  if (!n || n === s.stars && s.fuzzAt) return s;
  if (!(now > 0)) return { ...s, stars: n, fuzzAt: 0, fuzzLeft: Math.min(HEAT.FUZZ_S[n] * 1000, s.fuzzLeft != null ? s.fuzzLeft : Infinity) };   // no clock sent: it lands paused
  const at = now + HEAT.FUZZ_S[n] * 1000, cur = s.stars > 0 ? (s.fuzzLeft != null ? now + s.fuzzLeft : s.fuzzAt) : 0;
  return { ...s, stars: n, fuzzAt: cur ? Math.min(cur, at) : at, fuzzLeft: null };
}
const cooled = (s) => ({ ...s, stars: 0, fuzzAt: 0, fuzzLeft: null });
// Willie's tab: he takes HEAT.TAX of every contract's pay until it's paid off. Returns [what you keep, the new debt, what he took].
const taxed = (s, pay) => { const t = Math.min(HEAT.TAX, s.debt || 0, Math.max(0, pay)); return [pay - t, (s.debt || 0) - t, t]; };
// a fine you can't cover goes on the tab
const fined = (s, n) => { const paid = Math.min(s.mash, n); return { ...s, mash: s.mash - paid, debt: (s.debt || 0) + (n - paid) }; };
// THE RAID: every jar in the camp (never a trophy, never the lockbox) + by stars: 1 = spare parts, 2 = + the shelf, 3 = + the still
export function raidOf(s, stars) {
  const box = s.lockbox || { ing: {}, parts: [] }, keep = new Set(box.parts);
  const equipped = new Set(Object.values(s.rig || {}));
  const owned = Object.fromEntries(Object.entries(s.owned || {}).filter(([id]) => keep.has(id) || (stars < 3 && equipped.has(id))));
  const pantry = stars >= 2 ? {} : Object.fromEntries(Object.entries(s.pantry || {}).filter(([k]) => k !== "scrap" && k !== "feints"));   // scrap copper is a spare part; feints are jars
  // "every jar in the camp site": the satchel's, the aging shelf's, and the row under the still's spout (review 2026-10-31: a finished run kept its jars)
  let out = { ...s, jars: [], shelf: [], batch: null, batch2: null, owned, pantry, raid: { id: ((s.raid && s.raid.id) || 0) + 1, stars } };
  const eq2 = new Set(Object.values(s.rig2 || {}));
  out = { ...out, owned2: stars >= 3 ? {} : Object.fromEntries(Object.entries(s.owned2 || {}).filter(([id]) => eq2.has(id))) };   // still 2's spare parts too
  if (stars >= 3) out = { ...out, rig: { ...START_RIG }, rig2: { ...START_RIG2 }, broken: false, broken2: false, seized: true };   // the still goes (both of them; still 2 back to bare)
  return out;
}
function raided(s, now) {
  const n = s.stars;
  let out = stat(cooled(raidOf(s, n)), "raids");
  const what = n >= 3 ? words("heat.raid.3") : n === 2 ? words("heat.raid.2") : words("heat.raid.1");
  if (n >= 3 && out.mash < HEAT.NEW_STILL) {        // broke: the game notices - Willie laughs and loans you a still
    out = { ...out, seized: false, debt: (out.debt || 0) + HEAT.LOAN };
    return say(out, what + " " + words("heat.loan", { n: HEAT.LOAN, tax: HEAT.TAX, coin: COIN.name }), "still");
  }
  return say(out, what, "still");
}

// ── THE REPLAY PACK (2026-10-31) ─────────────────────────────
// THE AGING SHELF: how many steps a shelved jar has gained by `now` (never past AGE.MAX_STEPS for that jar, counting earlier aging)
// the best a jar may ever be (review 2026-10-31: aging lifted House Blend / rotgut / rushed / turbo jars past their caps)
const capOfRun = (b) => (b.rushed ? "rough" : RECIPES[b.recipe].deviance ? "fine" : RECIPES[b.recipe].capQ || (YEASTS[b.card && b.card.yeast] && YEASTS[b.card.yeast].capQ) || null);
const capOfJar = (j) => j.cap || (j.rushed ? "rough" : RECIPES[j.r] && (RECIPES[j.r].deviance ? "fine" : RECIPES[j.r].capQ)) || "prime";
export const agedSteps = (x, now) => Math.max(0, Math.min(AGE.MAX_STEPS - ((x.jar && x.jar.ag) || 0), Math.floor(Math.max(0, now - x.at) / (AGE.STEP_MIN * 60000))));
// the jar it is now: + PER rating a step, and a RATING.Q line crossed lifts its quality (never past prime: legendary needs a rare find)
export function agedJar(x, now) {
  const n = agedSteps(x, now), j = x.jar;
  if (!n) return j;
  const sc = Math.min(100, (Number.isFinite(j.sc) ? j.sc : RATING.Q[j.q] || 40) + n * AGE.PER);
  const QS = ["rough", "fine", "prime"];
  let q = j.q;
  const top = Math.min(QS.indexOf("prime"), QS.includes(capOfJar(j)) ? QS.indexOf(capOfJar(j)) : 2);
  for (const qq of QS) if (QS.indexOf(qq) > QS.indexOf(q) && QS.indexOf(qq) <= top && QS.includes(q) && sc >= (RATING.Q[qq] || 0)) q = qq;
  return { ...j, sc, q, ag: (j.ag || 0) + n };
}
// a trophy: legendary, or a perfect rating
export const trophyWorthy = (j) => !!j && !j.cursed && (j.q === "legendary" || (!j.ag && Number.isFinite(j.sc) && j.sc >= TROPHY.MIN_SCORE));   // a 100 straight off the still (aging doesn't count)
// DISTILLER RANK: the highest whose jobs AND best rating you've reached
export const bestScore = (s) => Math.max(0, ...Object.values(s.best || {}));
export const rankOf = (s) => { let r = 0; RANK.forEach((k, i) => { if (((s.stats && s.stats.jobs) || 0) >= k.jobs && bestScore(s) >= k.best) r = i; }); return r; };
const ranked = (s) => { const r = rankOf(s); return r > (s.rank || 0) ? say({ ...s, rank: r }, words("rank.up", { rank: RANK[r].name }), "jar_clean_shine") : s; };
// THE BLOOD MOON: which night it is (the whole night - across the cycle's edge - shares one number), and whether it's a blood one
export const nightId = (nowMs) => Math.floor(nowMs / 1000 / TUNING.DAY_S + 0.38);
const hash32 = (n) => { let h = (n | 0) ^ 0x9e3779b9; h = Math.imul(h ^ (h >>> 16), 0x85ebca6b); h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35); return (h ^ (h >>> 16)) >>> 0; };
export const isBloodMoon = (nowMs) => isNight(nowMs) && BLOOD.EVERY > 0 && hash32(nightId(nowMs)) % Math.max(1, Math.floor(BLOOD.EVERY)) === 0;
// what a contract pays here: master mode (the yard's) and the blood moon's seam
const payOf = (s, c, now) => Math.round((c.reward.mash || 0) * (s.master ? MASTER.PAY : 1) * (Number.isFinite(now) && isBloodMoon(now) ? BLOOD.PAY_K : 1));

// THE SECOND STILL (2026-10-31): every still rule is written for `batch` / `broken` / `bang`. Still 2 runs the SAME rules on its own
// fields by swapping them in, playing the action, and swapping back (each with its OWN parts: rig2 / owned2 swap in too, and k2 marks still 2's prices).
const swap2 = (s) => { const { k2, ...o } = { ...s, batch: s.batch2 === undefined ? null : s.batch2, broken: !!s.broken2, bang: s.bang2 || null, batch2: s.batch, broken2: !!s.broken, bang2: s.bang,
  rig: s.rig2 || { ...START_RIG2 }, owned: s.owned2 || {}, rig2: s.rig, owned2: s.owned }; return s.k2 ? o : { ...o, k2: true }; };   // k2 = "still 2's fields are in" (its own prices)
const STILL_ACTS = ["RUN", "SWAP", "FIRE", "CLEAR", "STOP", "BLEND", "REPAIR", "BUY", "EQUIP"];
export const stillOf = (s, k) => (k === 1 ? { batch: s.batch2 || null, broken: !!s.broken2, bang: s.bang2 || null } : { batch: s.batch, broken: !!s.broken, bang: s.bang });

// ── the reducer ──────────────────────────────────────────────
// CO-OP: a line that came from the YARD (a yard action, or the still itself on a TICK) is marked msg.yard -
// that's the only kind the keeper shares with her friends (her satchel, her catches, her gifts stay hers).
export function moonshineReducer(s, a) {
  if (a && a.viaMix) a = { ...a, viaMix: false };
  if (s && s.k2) { const { k2, ...rest } = s; s = rest; }   // the marker never lives outside the swap       // only the reducer itself may say "this came from a free blend" (a guest can't send it)
  const out = reduce(s, a);
  return out !== s && out.msg && out.msg !== s.msg && YARD_NEWS.includes(a.type) ? { ...out, msg: { ...out.msg, yard: true } } : out;
}
function reduce(s, a) {
  if (a.still === 1 && STILL_ACTS.includes(a.type)) {
    if ((s.stills || 1) < 2) return s;
    const inn = swap2(s), out = reduce(inn, { ...a, still: 0 });
    return out === inn ? s : swap2(out);          // nothing happened: the SAME state back (React skips the render)
  }
  switch (a.type) {
    case "LOAD": return hydrate(a.saved, a.now);

    case "GATHER": if (!INGREDIENTS[a.ing]) return s;
      if (a.ing === "wisp") return s.eyes && !(s.bag.wisp > 0) ? say({ ...s, bag: bump(s.bag, "wisp") }, words("wisp.caught"), "wisp") : s;   // one at a time, only through the Eyes
      return stat(say({ ...s, bag: bump(s.bag, a.ing) }, `Picked up: ${a.name || INGREDIENTS[a.ing].name}`, a.ing), "gathered");

    case "BANK": {
      const n = Object.values(s.bag).reduce((x, y) => x + y, 0);
      if (!n) return s;
      const { wisp = 0, ...finds } = s.bag;                                   // a Stray Wisp never hits the shelf: it pays
      let pantry = s.pantry;
      for (const [k, v] of Object.entries(finds)) pantry = bump(pantry, k, v);
      const what = Object.entries(finds).map(([k, v]) => `${v} ${INGREDIENTS[k].name}`).join(", ");
      let out = { ...s, pantry, bag: {}, mash: s.mash + wisp * WISP_PAY };
      if (wisp) out = stat(out, "wisps", wisp);
      return stat(say(out, [what && `Home safe - ${what} put away.`, wisp && words("wisp.home", { n: wisp * WISP_PAY, coin: COIN.name })].filter(Boolean).join(" "), wisp ? "wisp" : undefined), "banked");
    }

    case "CAUGHT": {
      const n = Object.values(s.bag).reduce((x, y) => x + y, 0);
      // the bag is always gone; the stay-in-until-dawn lock only when TUNING.SPOOK_LOCK (off for now - caP'n 2026-10-23)
      const lock = TUNING.SPOOK_LOCK || (typeof a.master === "boolean" ? a.master : !!s.master);   // MASTER MODE brings the lock back (the yard I'm in decides)
      // STARS: dying loses the fuzz too - but it costs (caP'n). a.away = I'm a guest: the stars are my friend's yard's (moonshineYard sends FINE)
      const hot = !a.away && s.stars > 0;
      const base = { ...(hot ? cooled(fined(s, HEAT.DEATH_FINE)) : s), bag: {}, spookedUntil: lock ? nextDawn(a.now) : 0 };
      return stat(say(base,
        (n ? `Caught! You dropped everything you were carrying (${n}).` : "Caught!") + (lock ? " You're too spooked to go back out until dawn." : " Shake it off.")
        + (hot ? " " + words("heat.death", { n: HEAT.DEATH_FINE, coin: COIN.name }) : "")), "caught");
    }

    case "TICK": {
      let out = s;
      // a.hold: you're working a FRIEND's yard - your own still waits for you (no drip, no boom while you're away)
      if (a.hold && out.batch && !out.batch.done) out = { ...out, batch: { ...out.batch, at: a.now } };
      // ...and my own tub doesn't catch while I'm at a friend's (the burn so far is kept; the clock since the last stir restarts)
      if (a.hold && out.tub && Number.isFinite(out.tub.stirAt) && tubPhase(out.tub, a.now) === "cooking") out = { ...out, tub: { ...out.tub, burn: tubBurn(out.tub, a.now), stirAt: a.now } };
      // Anima: the still heals slowly
      if (out.anima < TUNING.ANIMA_MAX) {
        const step = TUNING.ANIMA_REGEN_S * 1000;
        const n = Math.floor((a.now - out.animaAt) / step);
        if (n > 0) out = { ...out, anima: Math.min(TUNING.ANIMA_MAX, out.anima + n), animaAt: out.animaAt + n * step };
      }
      // the still: keep the run only if something happened (clog / pop / boom / dry)
      if (!a.hold && out.batch && !out.batch.done) { const r = settle(out, a.now); if (r.events.length) out = r.s; }
      // THE SECOND STILL: the same (it waits at a friend's; it's settled on its own fields)
      if (a.hold && out.batch2 && !out.batch2.done) out = { ...out, batch2: { ...out.batch2, at: a.now } };
      if (!a.hold && out.batch2 && !out.batch2.done) { const r = settle(swap2(out), a.now); if (r.events.length) out = swap2(r.s); }
      // THE FUZZ: at a friend's yard my own camp waits (the clock pauses); home again it picks up; when it runs out, they raid
      if (out.stars > 0) {
        if (a.hold) { if (out.fuzzLeft == null) out = { ...out, fuzzLeft: Math.max(0, (out.fuzzAt || a.now) - a.now) }; }
        else if (out.fuzzLeft != null) out = { ...out, fuzzAt: a.now + out.fuzzLeft, fuzzLeft: null };
        else if (out.fuzzAt && a.now >= out.fuzzAt) out = raided(out, a.now);
      }
      return out;
    }

    // 1 · MASH IN at the tub
    case "MASH_IN": {
      // FREE BLEND (2026-10-31): a.mix = any mix from the shelf. It makes the recipe whose needs it matches exactly - a secret one goes in the book
      if (a.mix && typeof a.mix === "object" && !s.tub) {
        const mix = {}; let n = 0;
        for (const [k, v] of Object.entries(a.mix)) {
          if (!(Number.isFinite(v) && v >= 1)) continue;
          if (!INGREDIENTS[k] || INGREDIENTS[k].material || INGREDIENTS[k].made || INGREDIENTS[k].errand) return s;   // scrap, feints, a wisp: not food
          mix[k] = Math.floor(v); n += mix[k];
        }
        if (!n || n > FREE.MAX || Object.entries(mix).some(([k, v]) => (s.pantry[k] || 0) < v)) return s;
        if (!FREE.WATER.some((k) => mix[k]) || !FREE.SWEET.some((k) => mix[k])) return say(s, words("free.wont"), "tub");
        const id = recipeOfMix(mix), found = RECIPES[id].hidden && !(s.book || {})[id];
        let pantry = s.pantry; for (const [k, v] of Object.entries(mix)) pantry = bump(pantry, k, -v);
        // a match: the normal mash-in takes exactly the mix (it IS the needs). The House Blend has no needs, so it takes the mix here.
        let next = reduce({ ...s, book: found ? { ...(s.book || {}), [id]: true } : s.book }, { ...a, mix: null, recipe: id, viaMix: true });
        if (id === "house_blend" && next.tub) {   // the catalyst must be left over AFTER the mix (review 2026-10-31: the honey was used twice)
          const cat = next.tub.catalyst && (pantry[next.tub.catalyst] || 0) >= 1 ? next.tub.catalyst : null;
          next = { ...next, tub: { ...next.tub, catalyst: cat }, pantry: tidy(cat ? bump(pantry, cat, -1) : pantry) };
        }
        if (!next.tub) return s;
        return found ? say(next, words("free.found", { name: RECIPES[id].name }), "tub") : id === "house_blend" ? say(next, words("free.house"), "tub") : next;
      }
      if (s.tub || !canBrew(s, a.recipe) || (!knownRecipe(s, a.recipe) && !a.viaMix)) return s;   // a secret recipe only once it's in the book; the House Blend only from a mix
      // a catalyst must be a real RARE find, and one must be left over after the recipe takes its share
      const need = RECIPES[a.recipe].needs;
      const cat = !RECIPES[a.recipe].deviance && isRare(a.catalyst) && (s.pantry[a.catalyst] || 0) - (need[a.catalyst] || 0) >= 1 ? a.catalyst : null;
      let pantry = s.pantry;
      for (const [k, n] of Object.entries(RECIPES[a.recipe].needs)) pantry = bump(pantry, k, -n);
      if (cat) pantry = bump(pantry, cat, -1);
      // EVERY STEP COUNTS: the jug's water line (a.water, 1 = spot on) and the yeast ride along. No water sent = an old caller = spot on.
      const water = Number.isFinite(a.water) ? Math.max(0, Math.min(MASH.WATER_MAX, a.water)) : null;
      const yeast = YEASTS[a.yeast] ? a.yeast : "bread";
      // stirring is only measured when the caller opts in (the HUD sends stir: true) - an older caller never stirs, so it would always burn
      const tub = { recipe: a.recipe, startedAt: a.now, catalyst: cat, size: s.khaoCap, malt: null, yeast };
      if (a.stir === true) { tub.stirAt = a.now; tub.burn = 0; }
      if (water != null) tub.water = water;
      const line = water == null || Math.abs(water - 1) <= MASH.WATER_OK ? "Right on the line." : water > 1 ? "A bit much water." : "A touch shy of the line.";
      return say({ ...s, pantry: tidy(pantry), tub }, `${RECIPES[a.recipe].name} mash is cooking. ${line} Keep it stirred.`);
    }

    // 1a · STIR (2026-10-25): knock the heat off the bottom before it catches. Only while it cooks.
    case "STIR": {
      const t = s.tub;
      if (!t || tubPhase(t, a.now) !== "cooking" || !Number.isFinite(t.stirAt)) return s;
      const burn = tubBurn(t, a.now), was = t.burn || 0;
      const out = { ...s, tub: { ...t, burn, stirAt: a.now } };
      return burn - was > 0.02 ? say(out, "Ugh - it caught on the bottom. Stir quicker.", "tub") : out;
    }

    // 1b · THE MALT (optional bonus): add it while the cooling mash is in the green window
    case "MALT": {
      if (!canMalt(s.tub, a.now)) return s;
      const k = maltShare(s.tub, a.now), good = k >= TUNING.MALT_WINDOW[0] && k <= TUNING.MALT_WINDOW[1];
      const out = say({ ...s, tub: { ...s.tub, malt: good ? "good" : "miss" } },
        good ? `Malt in, right on time. +${TUNING.MALT_BONUS} jar this run.` : k < TUNING.MALT_WINDOW[0] ? "Too hot - the malt cooked off. No bonus this time." : "Too cool - it barely took. No bonus this time.");
      return good ? stat(out, "malted") : out;
    }

    // 2 · POUR the cooked mash into a barrel: fermenting starts
    case "POUR": {
      if (!s.tub || tubPhase(s.tub, a.now) !== "cooked") return s;
      const i = Number.isInteger(a.barrel) && a.barrel >= 0 && a.barrel < s.barrels.length && !s.barrels[a.barrel] ? a.barrel : freeBarrel(s);
      if (i < 0) return say(s, "Every barrel is full - run one through the still first.");
      const barrels = [...s.barrels];
      // the marks land: the whole cook's burn, how hot it was poured (the yeast's pitch), and how much of it will convert
      const { stirAt, ...tub } = s.tub;
      const burn = tubBurn(s.tub, a.now), pitch = Number.isFinite(stirAt) ? Math.round(mashTemp(s.tub, a.now)) : null;
      const b = { ...tub, rushed: false, startedAt: a.now };
      if (burn != null) b.burn = burn;
      if (pitch != null) b.pitch = pitch;
      b.eff = mashEff(tub.water, pitch);
      barrels[i] = b;
      const z = pitch == null ? "good" : pitchZone(pitch);
      const caught = (burn || 0) > 0.02 ? " It caught on the bottom, too - you'll taste it." : "";
      return say({ ...s, tub: null, barrels }, (z === "hot" ? "Into the barrel - way too hot. That cooked the yeast." : z === "warm" ? "Into the barrel - a touch warm for the yeast." : z === "cold" ? "Into the barrel. Cold - it'll be slow to start." : "Into the barrel, just right. It's fermenting now.") + caught);
    }

    // rush a fermenting barrel with Anima: done now, comes out rough
    case "RUSH": {
      const i = Number.isInteger(a.barrel) ? a.barrel : s.barrels.findIndex((b) => b && barrelPhase(b, a.now) === "working");
      const b = s.barrels[i];
      if (!b || b.rushed || barrelPhase(b, a.now) !== "working" || s.anima < TUNING.RUSH_COST) return s;
      if (b.catalyst) return say(s, `Not this one - there's ${INGREDIENTS[b.catalyst].name} in it. Rushing would waste it.`);
      const barrels = [...s.barrels];
      barrels[i] = { ...b, rushed: true, startedAt: a.now - fermentMs(b) };
      return say({ ...spend(s, TUNING.RUSH_COST, a.now), barrels }, `Rushed! It's ready, and it'll show. (-${TUNING.RUSH_COST} Anima)`);
    }

    // 3 · RUN a ready barrel through the still. a.feints = how many Feints to pour in; a.charge = what loads the Thumper.
    case "RUN": {
      if (s.batch) return s;
      if (s.broken) return say(s, "The still's busted - fix it first.", "still");
      if (s.seized) return say(s, words("heat.seized", { n: HEAT.NEW_STILL, coin: COIN.name }), "still");
      if (NEEDED_SLOTS.some((k) => !s.rig[k])) return say(s, words("up.still2.bare"), "still");   // still 2 isn't built yet
      const i = Number.isInteger(a.barrel) && s.barrels[a.barrel] && barrelPhase(s.barrels[a.barrel], a.now) === "ready" ? a.barrel : readyBarrel(s, a.now);
      if (i < 0) return s;
      let pantry = { ...s.pantry };
      // the Thumper: only if you have one, and it needs loading (feints or fruit)
      let thump = false, flavor = null;
      if (s.rig.thumper === "thumper" && THUMP_CHARGE[a.charge] && (pantry[a.charge] || 0) >= 1) {
        thump = true; flavor = THUMP_CHARGE[a.charge].flavor; pantry[a.charge] -= 1;
      }
      const feints = Math.max(0, Math.min(STILL.FEINT_MAX, Math.floor(a.feints || 0), pantry.feints || 0));
      if (feints) pantry.feints -= feints;
      const barrels = [...s.barrels], b = barrels[i];
      barrels[i] = null;
      const run = newRun(b, s.rig, a.now, { thump, flavor, feints });
      const extras = [thump && "thumper loaded", feints && `${feints} feints in`, b.malt === "good" && "good malt"].filter(Boolean);
      return stat(say({ ...s, pantry: tidy(pantry), barrels, batch: run },
        `The still is running${extras.length ? ` (${extras.join(", ")})` : ""}.`, "still"), "runs");
    }

    // THE STILL, hands on: swap the jar under the spout, change the fire, clear a clog, stop early
    case "SWAP": case "FIRE": case "CLEAR": case "STOP": {
      if (!s.batch) return s;
      const { s: out } = settle(s, a.now);
      const b = out.batch;
      if (!b || b.done) return out;                         // it boomed or ran dry on the way here
      if (a.type === "SWAP") return b.clog ? say(out, "The line's blocked - nothing's dripping. Clear it first (C).") : b.cur >= STILL.ROW - 1 ? say(out, "That's the last jar in the row.") : { ...out, batch: swapJar(b) };
      if (a.type === "FIRE") return { ...out, batch: setFire(b, a.fire) };
      if (a.type === "CLEAR") return b.clog ? say({ ...out, batch: clearLine(b) }, "You rap the worm - the line clears. Phew.") : out;
      return say({ ...out, batch: stopRun(b) }, "You kill the fire. Whatever's left in the pot stays there. Time to blend.");
    }

    // 4 · BLEND: pour the picked jars together. The rest become Feints (or get tossed if they're poison).
    case "BLEND": {
      const { s: out } = settle(s, a.now);
      const b = out.batch;
      if (!b || !b.done) return out;
      const r = blend(b, a.picks, a.water);          // a.water = how much you watered it down (the proof)
      const jars = [...out.jars];
      const sc = r.jars > 0 ? r.rating.score : null, pf = r.jars > 0 ? r.proof : null;
      // CO-OP: a.share = the friends who brewed with you - the jars go round the circle, you first
      const share = Array.isArray(a.share) ? [...new Set(a.share.filter((u) => typeof u === "string" && u))].slice(0, 3) : [];
      const outbox = [...(out.outbox || [])];
      for (let i = 0; i < r.jars; i++) {
        const jar = { r: b.recipe, q: r.q, rushed: b.rushed, cursed: r.cursed, fl: r.flavor, sc, pf, ...(capOfRun(b) ? { cap: capOfRun(b) } : {}), ...(out.brand && out.brand.name ? { lb: out.brand.name } : {}) }, who = i % (share.length + 1);
        if (who === 0 || r.cursed) jars.push(jar); else outbox.push({ id: `g${Math.floor(a.now).toString(36)}x${i}`, to: share[who - 1], jar });   // nobody gets handed poison; the id makes a resend harmless
      }
      const given = outbox.length - (out.outbox || []).length;
      // WILLIE'S CARD: the rating, shown at the still (the yard's - everyone at the still sees it); my best for this recipe
      const card = r.jars > 0 ? { id: ((out.card && out.card.id) || 0) + 1, r: b.recipe, score: r.rating.score, parts: r.rating.parts, notes: r.rating.notes.slice(0, 6),
        jars: r.jars, max: r.max, proof: r.proof, q: r.q, cursed: r.cursed } : out.card;
      const prevBest = (out.best || {})[b.recipe] || 0, isBest = r.jars > 0 && !r.cursed && sc > prevBest;
      let next = stat({ ...out, jars, outbox, batch: null, card, best: isBest ? { ...(out.best || {}), [b.recipe]: sc } : out.best || {},
        pantry: r.feints ? bump(out.pantry, "feints", r.feints) : out.pantry }, "jarred", r.jars);
      const name = `${r.jars} jar${r.jars === 1 ? "" : "s"} of ${r.cursed ? "CURSED" : r.q} ${r.flavor ? FLAVORS[r.flavor] + " " : ""}${RECIPES[b.recipe].name}`
        + (r.jars && !r.cursed ? ` - Willie gives it ${sc}${sc >= 100 ? ". PERFECT. A true Moonshine Master." : isBest && prevBest ? " (your best yet)" : ""}` : "");
      const tail = (given ? ` ${given} went to your friends.` : "") + (r.feints ? ` ${r.feints} jar${r.feints === 1 ? "" : "s"} saved as feints.` : "");
      if (!r.jars) return say(next, `Nothing poured.${tail}`);
      const patient = !b.rushed && !r.cursed && !RECIPES[b.recipe].deviance && !(YEASTS[b.card && b.card.yeast] && YEASTS[b.card.yeast].capQ) && qRank(r.q) >= qRank("prime");
      if (patient && next.khaoCap < TUNING.KHAO_MAX) {
        next = { ...next, khaoCap: Math.min(TUNING.KHAO_MAX, next.khaoCap + TUNING.KHAO_GROWTH) };
        return say(next, `${name}. Patience grew your batch size!${tail}`, "jar_" + b.recipe);
      }
      return say(next, r.cursed ? `${name}. Nobody's drinking that.${tail}` : `${name}.${tail}`, "jar_" + b.recipe);
    }

    // pour the cursed jars out (they're no good to anyone)
    case "DUMP": {
      const n = s.jars.filter((j) => j.cursed).length;
      if (!n) return s;
      return stat(say({ ...s, jars: s.jars.filter((j) => !j.cursed) }, `Poured out ${n} cursed jar${n === 1 ? "" : "s"}. The grass looks worried.`), "dumped");
    }

    // ── THE RIG ──
    case "BUY": {
      const p = PARTS[a.slot] && PARTS[a.slot][a.part];
      if (!p || partOwned(s, a.slot, a.part) || s.mash < partPrice(s, a.slot, a.part) || s.seized) return s;
      if (s.batch) return say(s, "Finish this run first - nobody swaps parts on a hot still.");   // only THIS still (each has its own parts since 2026-10-31)
      return say({ ...s, mash: s.mash - partPrice(s, a.slot, a.part), owned: { ...s.owned, [a.part]: true }, rig: { ...s.rig, [a.slot]: a.part } }, `${p.name} - yours.`);
    }
    case "EQUIP": {
      if (s.batch || s.seized || !partOwned(s, a.slot, a.part) || s.rig[a.slot] === a.part) return s;
      const box = s.lockbox || { ing: {}, parts: [] };
      return say({ ...s, rig: { ...s.rig, [a.slot]: a.part }, lockbox: !s.k2 && box.parts.includes(a.part) ? { ...box, parts: box.parts.filter((x) => x !== a.part) } : box }, `${PARTS[a.slot][a.part].name} is on.`);
    }
    case "REPAIR": {
      if (!s.broken) return s;
      if (a.how === "scrap" && (s.pantry.scrap || 0) >= STILL.REPAIR.scrap)
        return say({ ...s, broken: false, pantry: tidy(bump(s.pantry, "scrap", -STILL.REPAIR.scrap)) }, "Hammered, patched, pasted. She'll run.", "still");
      if (a.how === "coin" && s.mash >= STILL.REPAIR.coin)
        return say({ ...s, broken: false, mash: s.mash - STILL.REPAIR.coin }, "New cap, new line. Good as new.", "still");
      return s;
    }

    // ── CHARMS (2026-10-25): pay with coin, ingredients or shine; the charm burns for everyone in the yard ──
    case "CHARM": {
      const c = CHARMS[a.id], now = Number.isFinite(a.now) ? a.now : 0;
      if (!c || !now) return s;
      if (s.charm && s.charm.until > now) return say(s, `${CHARMS[s.charm.id].name} is already burning.`);
      let out = null;
      if (a.how === "coin" && s.mash >= c.pay.coin) out = { ...s, mash: s.mash - c.pay.coin };
      else if (a.how === "ing" && Object.entries(c.pay.ing).every(([k, n]) => (s.pantry[k] || 0) >= n)) {
        let pantry = s.pantry; for (const [k, n] of Object.entries(c.pay.ing)) pantry = bump(pantry, k, -n);
        out = { ...s, pantry: tidy(pantry) };
      } else if (a.how === "shine" && !a.who) {         // a.who = a guest's request: THEIR jars are not in this yard's satchel, so a guest can't pay in shine yet
        const sh = c.pay.shine, ok = s.jars.map((j, i) => ({ j, i })).filter(({ j }) => !j.cursed && qRank(j.q) >= qRank(sh.minQ))
          .sort((x, y) => qRank(x.j.q) - qRank(y.j.q)).slice(0, sh.n);                 // the roughest jars that qualify go first
        if (ok.length === sh.n) { const gone = new Set(ok.map((x) => x.i)); out = { ...s, jars: s.jars.filter((_, i) => !gone.has(i)) }; }
      }
      if (!out) return say(s, "Not enough for that.");
      return say({ ...out, charm: { id: a.id, until: now + c.secs * 1000 } }, `${c.name} is lit - it burns ${c.secs} seconds. Go!`);
    }

    case "ACCEPT": {
      if (!offered(s).some((c) => c.id === a.id)) return s;
      const c = contractOf(a.id), hot = c && c.stars > 0;
      const out = { ...s, contracts: { ...s.contracts, [a.id]: "accepted" } };
      return hot ? say(heatUp(out, c.stars, Number.isFinite(a.now) ? a.now : 0), words("heat.on", { stars: "★".repeat(Math.max(s.stars, c.stars)), m: Math.round(HEAT.FUZZ_S[Math.max(s.stars, c.stars)] / 60) }), "still")
        : say(out, "Tome Contract accepted.");
    }

    case "DELIVER": {
      const c = contractOf(a.id);
      if (!c || s.contracts[c.id] !== "accepted") return s;
      const idx = pickJars(s.jars, c.req, heldProofs(s, c.id));
      if (!idx) {
        const onlyCursed = s.jars.length > 0 && s.jars.every((j) => j.cursed);
        return say(s, onlyCursed ? `${c.client} takes one sniff. "That's poison. Pour it out."` : "Not yet - you don't have the right jars for that.");
      }
      const gone = new Set(idx);
      const g = withSpecs(s, c);
      const [pay, debt, tax] = taxed(s, payOf(s, c, a.now));
      return ranked(stat(say(finishContract({
        ...g.s,
        jars: s.jars.filter((_, i) => !gone.has(i)),
        mash: s.mash + pay, debt,
      }, c), (isGen(c) ? words("done.line", { client: c.client, n: pay, coin: COIN.name }) : `Contract done! +${pay} ${COIN.name}`) + (tax ? " " + words("heat.tax", { n: tax, left: debt }) : "") + g.note), "jobs"));
    }

    // ── CO-OP (moonshineYard.js) ──
    // a friend brought their satchel home to YOUR yard
    case "STOCK": {
      let pantry = s.pantry, n = 0, wisps = 0;
      for (const [k, v] of Object.entries(a.items || {})) if (INGREDIENTS[k] && Number.isFinite(v) && v > 0) {
        const c = Math.min(999, Math.floor(v));
        if (k === "wisp") wisps += Math.min(4, c); else { pantry = bump(pantry, k, c); n += c; }   // a friend's wisp pays the TILL (the crew's coin)
      }
      if (!n && !wisps) return s;
      const who = String(a.who || "A friend").slice(0, 24);
      return say({ ...s, pantry, mash: s.mash + wisps * WISP_PAY }, [n && `${who} stocked the shelf (${n}).`, wisps && `${who} brought a wisp home: +${wisps * WISP_PAY} ${COIN.name} in the till.`].filter(Boolean).join(" "));
    }
    // you brought your satchel home to a FRIEND's yard: it's on their shelf now (the STOCK goes to them)
    case "BANK_AWAY": {
      const n = Object.values(s.bag).reduce((x, y) => x + y, 0);
      if (!n) return s;
      return stat(say({ ...(s.bag.wisp > 0 ? stat(s, "wisps", s.bag.wisp) : s), bag: {} }, `Home safe - ${n} on ${String(a.keeper || "your friend").slice(0, 24)}'s shelf.`), "banked");
    }
    // a.ids: the gifts that landed (each has its own id from BLEND - sending twice is harmless)
    // a friend hands the yard's buyer THEIR jars for a contract in the yard's book: the coin goes in the till
    case "DELIVER_JARS": {
      const c = contractOf(a.id), who = String(a.who || "A friend").slice(0, 24);
      const jars = (Array.isArray(a.jars) ? a.jars : []).map(cleanJar).filter(Boolean).slice(0, 12);
      if (!jars.length) return s;
      // the gift ids come from the hand-over's own key (unique), so two hand-overs in one instant can't overwrite each other
      const tag = typeof a.key === "string" && /^[A-Za-z0-9_-]{1,40}$/.test(a.key) ? a.key.replace(/[^A-Za-z0-9]/g, "").toLowerCase() : Math.floor(a.now || 0).toString(36);
      const idx = c && s.contracts[c.id] === "accepted" ? pickJars(jars, c.req, heldProofs(s, c.id)) : null;
      if (!idx) {                               // too late (someone beat them to it) or the wrong jars: they go home again
        const back = typeof a.by === "string" && a.by ? jars.map((jar, i) => ({ id: `r${tag}x${i}`, to: a.by.slice(0, 128), jar })) : [];
        return say({ ...s, outbox: [...(s.outbox || []), ...back] }, `${who}'s jars didn't fit that one - sent back.`);
      }
      const used = new Set(idx);
      const spare = typeof a.by === "string" && a.by ? jars.filter((_, i) => !used.has(i)).map((jar, i) => ({ id: `s${tag}x${i}`, to: a.by.slice(0, 128), jar })) : [];
      const g = withSpecs(s, c);
      const [pay, debt, tax] = taxed(s, payOf(s, c, a.now));
      return say(finishContract({ ...g.s, mash: s.mash + pay, debt, outbox: [...(s.outbox || []), ...spare] }, c),
        `${who} delivered "${c.ask}"! +${pay} ${COIN.name} in the till.` + (tax ? " " + words("heat.tax", { n: tax, left: debt }) : "") + g.note);
    }
    // a contract the crew finished together: checked off in MY book too (the coin went in the shared till)
    case "CONTRACT_DONE": {
      const c = contractOf(a.id);
      if (!c || isGen(c) || s.contracts[c.id] === "done") return s;          // (an endless contract just leaves the book - nothing to tick off at home)
      const g = withSpecs(s, c);
      return say({ ...g.s, contracts: { ...s.contracts, [c.id]: "done" } }, `Checked off together: "${c.ask}".${g.note}`);
    }
    // send an endless visitor away without a payout: the seat moves on to the next mission (a story contract or an egg can't be sent away)
    case "ABANDON": {
      const c = contractOf(a.id);
      if (!c || !isGen(c) || s.contracts[c.id] !== "accepted") return s;
      const { [c.id]: _gone, ...rest } = s.contracts;
      return say({ ...s, contracts: rest, board: nextBoard(s, c) }, words("away.line", { client: c.client }));
    }
    // THE UNSTABLE SIGHTS (2026-10-30): you looked closer at one (only through the Eyes; once per save; personal)
    case "SIGHT": {
      if (!s.eyes || !SIGHTS.some((g) => g.id === a.id) || s.sights[a.id]) return s;
      const sights = { ...s.sights, [a.id]: true }, all = SIGHTS.every((g) => sights[g.id]);
      return stat(say({ ...s, sights }, words(`sight.${a.id}.line`) + (all ? " " + words("sight.all") : ""), "eyes"), "sights");
    }
    // put the Spectacles on / take them off (personal: never goes to the keeper). Needs the Spectacles.
    case "EYES": {
      if (!s.specs) return s;
      const on = typeof a.on === "boolean" ? a.on : !s.eyes;
      if (on === s.eyes) return s;
      // 2026-10-30 (review): once they're open out there, they STAY open until you're home - otherwise you could grab a tier-3
      // flower or a wisp with the Eyes on and shut them for an easy run back (that would undo "Dead Calm is required").
      if (!on && a.out) return say(s, "Not out here - the Eyes don't close until you're back past the lanterns.", "eyes");
      return say({ ...s, eyes: on }, on ? "The Unstable Eyes open. Everything out there can see you too." : "You take the Spectacles off.");
    }
    // my jars, handed over for a friend's yard contract, came straight back (it never left)
    case "RETURN_JARS": {
      const jars = (Array.isArray(a.jars) ? a.jars : []).map(cleanJar).filter(Boolean);
      return jars.length ? say({ ...s, jars: [...s.jars, ...jars] }, "Couldn't reach your friend's yard - the jars are still yours.") : s;
    }
    // I hand over these jars (by index) for a contract in a friend's yard (the keeper decides; misfits come back as gifts)
    case "HAND_OVER": {
      const gone = new Set(Array.isArray(a.idx) ? a.idx : []);
      return { ...s, jars: s.jars.filter((_, i) => !gone.has(i)) };
    }
    // ── HANDS IN THE POST (2026-10-25, the pinned "hand-over in flight" bug) ──
    // I hand my satchel / jars to a friend's yard: they leave my hands NOW, but ride in `hands` (in the save) until the keeper's receipt
    // lands - so closing the tab in that split second loses nothing; the next visit finishes the delivery or takes them back.
    case "HAND_ADD": { const h = cleanHand(a.hand); return h && !(s.hands || []).some((x) => x.id === h.id) ? { ...s, hands: [...(s.hands || []), h].slice(-16) } : s; }
    case "HAND_DONE": return (s.hands || []).some((x) => x.id === a.id) ? { ...s, hands: s.hands.filter((x) => x.id !== a.id) } : s;
    // it never landed (the post was refused, or the yard it was meant for is long gone and nobody played it): it's yours again
    case "HAND_BACK": {
      const h = (s.hands || []).find((x) => x.id === a.id);
      if (!h) return s;
      const rest = { ...s, hands: s.hands.filter((x) => x.id !== a.id) };
      if (h.kind === "STOCK") { let bag = rest.bag; for (const [k, v] of Object.entries(h.items)) bag = bump(bag, k, v); return say({ ...rest, bag }, "Your friend's yard wasn't there to take it - your satchel's still on you."); }
      return say({ ...rest, jars: [...rest.jars, ...h.jars] }, "Your friend's yard wasn't there to take them - the jars are still yours.");
    }
    case "OUTBOX_SENT": { const gone = new Set(Array.isArray(a.ids) ? a.ids : []); return { ...s, outbox: (s.outbox || []).filter((o) => !gone.has(o.id)) }; }
    // the STOCK never left (offline): your satchel comes back
    case "UNBANK": {
      let bag = s.bag;
      for (const [k, v] of Object.entries(a.items || {})) if (INGREDIENTS[k] && Number.isFinite(v) && v > 0) bag = bump(bag, k, Math.floor(v));
      return say({ ...s, bag }, "Couldn't reach your friend's yard - your satchel's still on you.");
    }
    case "GIFT_JAR": {
      const j = cleanJar(a.jar);
      if (!j) return s;
      return say({ ...s, jars: [...s.jars, j] }, `${String(a.from || "A friend").slice(0, 24)} handed you a jar of ${j.q} ${RECIPES[j.r].name}.`, "jar_" + j.r);   // not "jarred": your tutorial counts the runs YOU made
    }

    // ── STARS + REVENUERS (2026-10-31) ──
    // the whole crew stayed out past the lanterns long enough (World3D / App measure it; the keeper's - or solo - screen sends it)
    case "STARS_CLEAR": return s.stars > 0 ? stat(say(cooled(s), words("heat.clear"), "still"), "cleared") : s;
    // a friend died out there while we had stars (sent to the keeper's yard): the stars go, the till pays the fine
    case "FINE": return s.stars > 0 ? say(cooled(fined(s, HEAT.DEATH_FINE)), words("heat.fine", { who: String(a.who || "A friend").slice(0, 24), n: HEAT.DEATH_FINE, coin: COIN.name })) : s;
    // THE LOCKBOX: an ingredient (a.ing, a.n) or a spare part (a.part) goes in / comes out
    case "LOCK": case "UNLOCK": {
      const box = s.lockbox || { ing: {}, parts: [] }, inn = a.type === "LOCK";
      if (a.part) {
        const real = typeof a.part === "string" && Object.values(PARTS).some((sl) => sl[a.part] && sl[a.part].price > 0);
        if (!real || !s.owned[a.part]) return s;
        if (inn) {
          if (box.parts.includes(a.part) || Object.values(s.rig).includes(a.part)) return s;
          if (box.parts.length >= lockCap(s).parts) return say(s, words("heat.box.full"));
          return say({ ...s, lockbox: { ...box, parts: [...box.parts, a.part] } }, words("heat.box.in"));
        }
        return box.parts.includes(a.part) ? { ...s, lockbox: { ...box, parts: box.parts.filter((x) => x !== a.part) } } : s;
      }
      const k = a.ing, n = Math.max(1, Math.floor(Number.isFinite(a.n) ? a.n : 1));
      if (!INGREDIENTS[k] || INGREDIENTS[k].errand || INGREDIENTS[k].made) return s;   // feints are jars, not ingredients
      const held = Object.values(box.ing).reduce((x, y) => x + y, 0);
      if (inn) {
        const m = Math.min(n, s.pantry[k] || 0, lockCap(s).ing - held);
        if (m <= 0) return held >= lockCap(s).ing ? say(s, words("heat.box.full")) : s;
        return { ...s, pantry: tidy(bump(s.pantry, k, -m)), lockbox: { ...box, ing: bump(box.ing, k, m) } };
      }
      const m = Math.min(n, box.ing[k] || 0);
      return m > 0 ? { ...s, pantry: bump(s.pantry, k, m), lockbox: { ...box, ing: tidy(bump(box.ing, k, -m)) } } : s;
    }
    // the revenuers took the still: buy a new one (the plain rig), or - broke - take Willie's loan
    case "NEW_STILL": {
      if (!s.seized || s.mash < HEAT.NEW_STILL) return s;
      return say({ ...s, seized: false, mash: s.mash - HEAT.NEW_STILL, rig: { ...START_RIG } }, words("heat.newstill"), "still");
    }
    case "LOAN": {
      if (!s.seized || s.mash >= HEAT.NEW_STILL) return s;
      return say({ ...s, seized: false, rig: { ...START_RIG }, debt: (s.debt || 0) + HEAT.LOAN }, words("heat.loan", { n: HEAT.LOAN, tax: HEAT.TAX, coin: COIN.name }), "still");
    }
    // ONE BUSTED = ALL BUSTED: the friend's yard I'm working got raided - my own jars (personal, not the yard's) go too.
    // Personal (NOT a yard action): App sends it to MY state when the yard's raid id changes while I'm a guest. Nothing else is touched.
    case "RAIDED": return s.jars && s.jars.length ? say({ ...s, jars: [] }, words("heat.raided"), "still") : s;

    // ── THE REPLAY PACK (2026-10-31) ──
    // THE AGING SHELF: a jar goes up (a.i = its index in my jars) / comes down (a.k = its shelf slot) - it keeps what it gained
    case "SHELVE": {
      if (!(a.now > 0)) return s;            // no clock, no shelf (it would age at once)
      const j = s.jars[a.i], now = a.now;
      if (!j || j.cursed || (s.shelf || []).length >= AGE.SLOTS) return (s.shelf || []).length >= AGE.SLOTS ? say(s, words("age.full")) : s;
      if ((j.ag || 0) >= AGE.MAX_STEPS) return say(s, words("age.done"));
      return say({ ...s, jars: s.jars.filter((_, k) => k !== a.i), shelf: [...(s.shelf || []), { jar: j, at: now }] }, words("age.in"), "jar_" + j.r);
    }
    case "UNSHELVE": {
      const x = (s.shelf || [])[a.k], now = Number.isFinite(a.now) ? a.now : 0;
      if (!x) return s;
      const j = agedJar(x, now), up = j.q !== x.jar.q;
      return say({ ...s, shelf: s.shelf.filter((_, k) => k !== a.k), jars: [...s.jars, j] }, up ? words("age.up", { q: j.q }) : agedSteps(x, now) ? words("age.better", { n: j.sc - (x.jar.sc ?? j.sc - agedSteps(x, now) * AGE.PER) }) : words("age.out"), "jar_" + j.r);
    }
    // THE TROPHY SHELF: on show (legendary or a perfect 100) / back in the satchel
    case "TROPHY": {
      const j = s.jars[a.i];
      if (!trophyWorthy(j)) return s;
      if ((s.trophies || []).length >= TROPHY.SLOTS) return say(s, words("trophy.full"));
      return say({ ...s, jars: s.jars.filter((_, k) => k !== a.i), trophies: [...(s.trophies || []), j] }, words("trophy.in"), "jar_" + j.r);
    }
    case "UNTROPHY": { const j0 = (s.trophies || [])[a.k], j = j0 && { ...j0, tr: true }; return j ? { ...s, trophies: s.trophies.filter((_, k) => k !== a.k), jars: [...s.jars, j] } : s; }
    // MASTER MODE on / off (not while you're spooked - that would be a way out)
    case "MASTER": {
      const on = typeof a.on === "boolean" ? a.on : !s.master;
      if (on === !!s.master) return s;
      if (!on && Number.isFinite(a.now) && isSpooked(s, a.now)) return say(s, words("master.stuck"));
      if (acceptedList(s).length) return say(s, words("master.busy"));   // review 2026-10-31: flipping it on at the board for the pay, off for the woods
      return say({ ...s, master: on }, on ? words("master.on", { k: MASTER.PAY }) : words("master.off"));
    }
    // MY JAR LABEL: the brand name and the drawing
    case "BRAND": return { ...s, brand: { name: a.name != null ? cleanName(a.name) : (s.brand || {}).name || "", art: a.art != null ? cleanArt(a.art) : (s.brand || {}).art || "" } };

    // THE YARD UPGRADES (2026-10-31, ROADMAP 40): a.what = "camp" (the next size) | "shack" | "still2" | "decor" (a.id)
    case "UPGRADE": {
      let price = null, next = null, line = "";
      if (a.what === "camp") { const n = (s.camp || 0) + 1; if (!CAMP[n]) return s; price = CAMP[n].price; next = { camp: n }; line = words("up.camp", { name: CAMP[n].name }); }
      else if (a.what === "shack") { const n = (s.shack || 0) + 1, row = YARD_UP.shack[n]; if (!row) return s; if ((s.camp || 0) < (row.camp || 0)) return say(s, words("up.shack.room")); price = row.price; next = { shack: n }; line = words("up.shack"); }
      else if (a.what === "still2") { if ((s.stills || 1) >= 2) return s; price = YARD_UP.still2.price; next = { stills: 2, batch2: null, broken2: false, bang2: null }; line = words("up.still2"); }
      else if (a.what === "decor") { const d = DECOR[a.id]; if (!d || (s.decor || {})[a.id]) return s; price = d.price; next = { decor: { ...(s.decor || {}), [a.id]: true } }; line = words("up.decor", { name: d.name }); }
      else return s;
      if (s.mash < price) return say(s, "Not enough for that.");
      return say({ ...s, ...next, mash: s.mash - price }, line);
    }

    case "NOTE": return say(s, a.text, a.icon);          // a heads-up from the yard (QoL pings) - nothing changes but the toast

    // ── THE TUTORIAL's bookkeeping (what each step MEANS lives in tutorial.js) ──
    case "TUT_SEEN": return s.tut.seen[a.key] ? s : { ...s, tut: { ...s.tut, seen: { ...s.tut.seen, [a.key]: true } } };
    case "TUT_GIFT": {                       // Willie's starter sack - once per save (and ONE top-up if you'd be stuck without it)
      if (s.tut.gift && (!a.again || s.tut.seen.regift)) return s;
      let pantry = s.pantry;
      for (const [k, n] of Object.entries(a.items || {})) if (INGREDIENTS[k]) pantry = bump(pantry, k, n);
      const next = { ...s, pantry, tut: { ...s.tut, gift: true, seen: a.again ? { ...s.tut.seen, regift: true } : s.tut.seen } };
      return a.again ? say(next, "Willie tops up your sack.") : next;          // the first sack: Willie's own line already says it
    }
    case "TUT_STEP": return { ...s, tut: { ...s.tut, step: Math.max(0, a.step | 0), done: !!a.done, mark: { ...s.stats } } };   // mark: what "new" means for the next step
    case "TUT_SKIP": return { ...s, tut: { ...s.tut, done: true } };
    case "TUT_REPLAY": return { ...s, tut: { ...s.tut, step: 0, done: false, seen: s.tut.seen.regift ? { regift: true } : {} } };   // the one top-up stays spent
    // the still waits for you: time on the title screen doesn't drip (critic 2026-10-23)
    case "WAKE": {          // the game starts again: the still(s) and the fuzz pick up from NOW
      let o = s.batch && !s.batch.done ? { ...s, batch: { ...s.batch, at: a.now } } : s;
      if (o.batch2 && !o.batch2.done) o = { ...o, batch2: { ...o.batch2, at: a.now } };
      if (o.stars > 0 && o.fuzzLeft != null) o = { ...o, fuzzAt: a.now + o.fuzzLeft, fuzzLeft: null };
      return o;
    }

    default: return s;
  }
}

export { QUALITY };
